import { createPortal } from "react-dom";
import React, { useEffect, useRef, useState } from "react";
import { Plus, X, Footprints, Scale, Syringe, Camera, Pencil, NotepadText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KIND_META = {
  passeggiata: { icon: Footprints, color: "#34D399", label: "Passeggiata" },
  peso: { icon: Scale, color: "#FFB020", label: "Peso" },
  vaccino: { icon: Syringe, color: "#FF3B30", label: "Vaccino" },
  foto: { icon: Camera, color: "#FFB020", label: "Foto" },
  nota: { icon: NotepadText, color: "#34D399", label: "Nota" },
  disegno: { icon: Pencil, color: "#FF3B30", label: "Disegno" },
};

export default function Diary({ dogId }) {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (!dogId) return setEntries([]);
    const { data } = await api.get("/diary", { params: { dog_id: dogId } });
    setEntries(data);
  };
  useEffect(() => { load(); }, [dogId]);

  const del = async (id) => {
    if (!confirm("Eliminare?")) return;
    await api.delete(`/diary/${id}`);
    toast.success("Eliminato");
    load();
  };

  if (!dogId) {
    return <div className="text-[#9A9A9A] text-sm py-10 text-center">Seleziona un cane.</div>;
  }

  return (
    <div className="relative">
      <div className="flex justify-end mb-4">
        <button
          data-testid="add-entry-btn"
          onClick={() => setOpen(true)}
          className="orio-cta-btn relative px-4 py-2 rounded-full text-black font-semibold text-sm overflow-hidden"
        >
          <span className="relative z-10 inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" />Nuova voce</span>
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-16 text-[#9A9A9A] text-sm">
          Nessuna voce nel diario. Inizia a raccontare la storia del tuo cane.
        </div>
      ) : (
        <Timeline entries={entries} onDelete={del} />
      )}

      {open && <EntryDialog dogId={dogId} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function Timeline({ entries, onDelete }) {
  return (
    <div className="relative pl-6 sm:pl-10">
      <div className="absolute left-2 sm:left-4 top-0 bottom-0 w-px bg-gradient-to-b from-[#FF3B30] via-[#FFB020] to-[#34D399] opacity-40" />
      <div className="space-y-5">
        {entries.map((e) => {
          const meta = KIND_META[e.kind] || KIND_META.nota;
          const Icon = meta.icon;
          return (
            <div key={e.id} className="relative">
              <div className="absolute -left-6 sm:-left-10 top-2 h-5 w-5 sm:h-7 sm:w-7 rounded-full flex items-center justify-center"
                style={{ background: meta.color, color: "#000", boxShadow: `0 0 16px ${meta.color}80` }}>
                <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              </div>
              <div className="bg-white border border-[#F0EBE3] rounded-2xl p-4">
                <div className="flex items-start gap-2 justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
                    <span className="text-[10px] text-[#9A9A9A]">·</span>
                    <span className="text-[10px] text-[#9A9A9A]">{e.happened_at ? new Date(e.happened_at).toLocaleString("it-IT") : ""}</span>
                  </div>
                  <button onClick={() => onDelete(e.id)} className="text-[#9A9A9A] hover:text-[#FF3B30]">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="mt-1.5 font-medium text-sm">{e.title}</div>
                {e.value != null && <div className="text-xs text-[#9A9A9A] mt-0.5">{e.value} kg</div>}
                {e.note && <p className="mt-1.5 text-xs text-[#6B6B6B] leading-relaxed">{e.note}</p>}
                {e.photo_url && <img src={e.photo_url} alt="" className="mt-3 w-full max-w-xs rounded-xl border border-[#F0EBE3]" />}
                {e.drawing_data && <img src={e.drawing_data} alt="disegno" className="mt-3 w-full max-w-xs rounded-xl bg-white border border-[#F0EBE3]" />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EntryDialog({ dogId, onClose, onDone }) {
  const [kind, setKind] = useState("passeggiata");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [value, setValue] = useState("");
  const [photo, setPhoto] = useState(null);
  const [drawing, setDrawing] = useState(null);
  const [showDraw, setShowDraw] = useState(false);
  const [happenedAt, setHappenedAt] = useState(new Date().toISOString().slice(0, 16));
  const [loading, setLoading] = useState(false);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Max 2MB");
    const r = new FileReader();
    r.onload = () => setPhoto(r.result);
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Inserisci un titolo");
    setLoading(true);
    try {
      await api.post("/diary", {
        dog_id: dogId, kind, title,
        note: note || null,
        value: value ? parseFloat(value) : null,
        photo_url: photo,
        drawing_data: drawing,
        happened_at: happenedAt ? new Date(happenedAt).toISOString() : null,
      });
      toast.success("Aggiunto al diario");
      onDone();
    } catch { toast.error("Errore"); }
    finally { setLoading(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1200] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white border border-[#F0EBE3] rounded-3xl w-full max-w-md p-5 sm:p-6 max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl">Nuova voce del diario</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-[#F4EFE6] flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-1.5 mb-4">
          {Object.entries(KIND_META).map(([k, m]) => {
            const Icon = m.icon;
            const active = kind === k;
            return (
              <button key={k} onClick={() => { setKind(k); if (k === "disegno") setShowDraw(true); }}
                data-testid={`kind-${k}`}
                className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border transition-all text-[10px]`}
                style={{
                  background: active ? `${m.color}20` : "#FAF7F0",
                  borderColor: active ? `${m.color}60` : "#F0EBE3",
                  color: active ? m.color : "#1A1A1A",
                }}>
                <Icon className="h-4 w-4" />
                {m.label}
              </button>
            );
          })}
        </div>

        <input data-testid="entry-title" placeholder="Titolo (es. Parco Sempione, 2km)" value={title} onChange={(e) => setTitle(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none mb-2.5" />
        {kind === "peso" && (
          <input type="number" step="0.1" placeholder="Peso in kg" value={value} onChange={(e) => setValue(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none mb-2.5" />
        )}
        <textarea placeholder="Note (opzionale)" value={note} onChange={(e) => setNote(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none mb-2.5 min-h-[70px]" />
        <input type="datetime-local" value={happenedAt} onChange={(e) => setHappenedAt(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] outline-none mb-2.5" />
        <div className="flex gap-2 mb-3">
          <label className="flex-1 px-3 py-2 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] text-xs flex items-center justify-center gap-2 cursor-pointer hover:bg-[#F4EFE6]">
            <Camera className="h-3.5 w-3.5" /> {photo ? "Cambia foto" : "Foto"}
            <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
          </label>
          <button onClick={() => setShowDraw(true)}
            className="flex-1 px-3 py-2 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] text-xs flex items-center justify-center gap-2">
            <Pencil className="h-3.5 w-3.5" /> {drawing ? "Modifica disegno" : "Disegna"}
          </button>
        </div>
        {photo && <img src={photo} alt="" className="mb-3 max-h-32 rounded-lg" />}
        {drawing && <img src={drawing} alt="disegno" className="mb-3 max-h-32 rounded-lg bg-white" />}

        <button data-testid="entry-save" onClick={submit} disabled={loading}
          className="orio-cta-btn relative w-full px-5 py-3 rounded-full text-white font-semibold disabled:opacity-50 overflow-hidden">
          <span className="relative z-10">{loading ? "Salvataggio..." : "Salva nel diario"}</span>
        </button>

        {showDraw && <DrawCanvas onSave={(data) => { setDrawing(data); setShowDraw(false); }} onClose={() => setShowDraw(false)} />}
      </div>
    </div>
  , document.body);
}

function DrawCanvas({ onSave, onClose }) {
  const ref = useRef(null);
  const [color, setColor] = useState("#FF3B30");
  const drawing = useRef(false);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
  }, []);

  const point = (e) => {
    const canvas = ref.current;
    const r = canvas.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: ((cx - r.left) / r.width) * canvas.width, y: ((cy - r.top) / r.height) * canvas.height };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = ref.current.getContext("2d");
    const p = point(e);
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const p = point(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };
  const save = () => onSave(ref.current.toDataURL("image/png"));

  const colors = ["#FF3B30", "#FFB020", "#34D399", "#000000"];

  return createPortal(
    <div className="fixed inset-0 z-[1300] bg-black/90 backdrop-blur flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl">Disegna</h3>
          <div className="flex gap-1.5">
            {colors.map((c) => (
              <button key={c} onClick={() => setColor(c)}
                className="h-7 w-7 rounded-full border-2"
                style={{ background: c, borderColor: color === c ? "#fff" : "transparent" }} />
            ))}
          </div>
        </div>
        <canvas
          ref={ref}
          width={400}
          height={400}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          className="w-full bg-white rounded-2xl touch-none"
        />
        <div className="flex gap-2 mt-3">
          <button onClick={clear} className="flex-1 px-4 py-2.5 rounded-full bg-[#F4EFE6] text-sm">Pulisci</button>
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full bg-[#F4EFE6] text-sm">Annulla</button>
          <button onClick={save}
            className="orio-cta-btn relative flex-1 px-4 py-2.5 rounded-full text-black font-semibold text-sm overflow-hidden">
            <span className="relative z-10">Usa disegno</span>
          </button>
        </div>
      </div>
    </div>
  , document.body);
}
